import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import * as S from '../styles';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OutboxIcon from '@mui/icons-material/Outbox';

import { useTheme } from 'styled-components';
import ModalWarning from './ModalWarning';
import { myCoursesServiceNewStudent } from '../../../../../../../../services/newStudent';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../../ProviderNewStudent';
import {
  IExam,
  IListOfExams,
} from '../../../../../../../../services/newStudent/myCourses/myCourses.service';
import { IClassInfo } from '../../../../../../../../pages/newStudent/MyCoursesNewStudent/components/Subjects';
import type { AssessmentDetail, ISubjectContentData } from '../../../..';
import {
  IContentSubject,
  IContentSubjectGraduation,
  IDataContentSubject,
} from '../../../../../../../../types/newStudent/myCourses.interface';
import {
  AcademicProjectKind,
  isAcademicProjectKind,
} from '../../../../../../../../util/newStudent/academicProject';
import { toastErrorMessage } from '../../../../../../../../util/newStudent';

const deliveryKeys: AcademicProjectKind[] = [
  'evaluativeActivity',
  'assessmentWork',
  'tcc',
  'internship',
];

function collectDeliveries(
  data: IContentSubject | IContentSubjectGraduation[] | undefined,
): Array<IDataContentSubject & { type: AcademicProjectKind }> {
  if (!data) {
    return [];
  }

  const buckets: IContentSubject[] = Array.isArray(data)
    ? data.flatMap((unit) => (unit.class ?? []).map((item) => item.resources))
    : [data];

  return buckets.flatMap((resources) =>
    deliveryKeys.flatMap((key) =>
      (resources?.[key] ?? [])
        .filter((item) => Boolean(item?.title))
        .map((item) => ({ ...item, type: key })),
    ),
  );
}

interface Props {
  setSubjectExam: Dispatch<SetStateAction<IExam>>;
  setSubjectContent: Dispatch<SetStateAction<ISubjectContentData>>;
  setBlockedContent: Dispatch<SetStateAction<boolean>>;
  blockedContent: boolean;
  classInfo: IClassInfo;
  contentIdSelected: number;
  subjectContent: ISubjectContentData;
  assessmentDetail: AssessmentDetail;
  setAssessmentDetail: Dispatch<SetStateAction<AssessmentDetail>>;
}
export default function TabExam({
  setSubjectExam,
  setSubjectContent,
  setBlockedContent,
  classInfo,
  contentIdSelected,
  subjectContent,
  assessmentDetail,
  setAssessmentDetail,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [examInfo, setExamInfo] = useState<IListOfExams>();
  const [deliveries, setDeliveries] = useState<
    Array<IDataContentSubject & { type: AcademicProjectKind }>
  >([]);
  const [modalWarningIsOpen, setModalWarningIsOpen] = useState<boolean>(false);
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);

  const theme = useTheme();

  const courseId = classInfo.courseId;
  const disciplineId = classInfo.id;
  const contentId = contentIdSelected;

  useEffect(() => {
    if (!contentId) {
      return;
    }

    setIsLoading(true);
    const fetch = async () => {
      try {
        const [examsResult, contentResult] = await Promise.all([
          myCoursesServiceNewStudent.listExams(
            +courseId,
            +disciplineId,
            +contentId,
            undefined,
            true,
          ),
          myCoursesServiceNewStudent.getContent(
            courseId,
            disciplineId,
            contentId,
            true,
          ),
        ]);

        const nextDeliveries = contentResult.result
          ? collectDeliveries(contentResult.data)
          : [];
        setDeliveries(nextDeliveries);

        if (examsResult.result) {
          setHasGenerated(false);
          setExamInfo(examsResult.data);
        } else if (nextDeliveries.length === 0) {
          toastErrorMessage(examsResult.message);
        }
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [courseId, disciplineId, contentId]);

  const handleGenerateExam = async (title: string, resourceId?: number) => {
    try {
      setIsLoading(true);
      const { result, data } = await myCoursesServiceNewStudent.getExam(
        +courseId,
        +disciplineId,
        +contentId,
        0,
        resourceId,
      );
      if (result) {
        setAssessmentDetail('exam');
        setSubjectExam({ ...data, title });
        setHasGenerated(true);
        setBlockedContent(true);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenExam = (
    id: number,
    examType: number,
    title: string,
    response?: boolean,
  ) => {
    const fetch = async () => {
      try {
        setIsLoading(true);
        if (response) {
          const { result, data } =
            await myCoursesServiceNewStudent.getAnswerSheet(+id, +examType);

          if (result) {
            setAssessmentDetail('exam');
            setSubjectExam({ ...data, title });
          }
        } else {
          const { result, data } = await myCoursesServiceNewStudent.getExam(
            +courseId,
            +disciplineId,
            +contentId,
            +id,
          );

          if (result) {
            setAssessmentDetail('exam');
            setSubjectExam({ ...data, title });
          }
        }
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  };

  const isApproved = (title: string) => {
    return title.trim().toLowerCase().includes('aprovado');
  };
  return (
    <S.Container className="tab_exam">
      {examInfo?.exams?.map((item, index) => (
        <S.DivSubtitle key={index}>
          <S.DivSubtitleSeparated>
            <DescriptionOutlinedIcon
              fontSize="large"
              style={{
                color: theme.colors?.danger,
              }}
            />
            <S.Subtitle
              isApproved={isApproved(item.title)}
              onClick={() =>
                !isApproved(item.title) &&
                handleOpenExam(
                  item.id,
                  item.examType,
                  item.title,
                  item.response,
                )
              }
            >
              {item.title}
            </S.Subtitle>
            {!isApproved(item.title) && (
              <S.Separated> {item.attempt}ª Tentativa</S.Separated>
            )}
          </S.DivSubtitleSeparated>
        </S.DivSubtitle>
      ))}
      {deliveries.map((item, index) => (
        <S.DivSubtitle key={`${item.type}-${item.resourceId ?? index}`}>
          <S.DivSubtitleSeparated>
            <OutboxIcon
              fontSize="large"
              style={{
                color: theme.colors?.text.danger,
              }}
            />
            <S.Subtitle
              $selected={
                assessmentDetail === 'delivery' &&
                isAcademicProjectKind(subjectContent?.type) &&
                subjectContent?.type === item.type &&
                String(subjectContent?.resourceId ?? '') ===
                  String(item.resourceId ?? '')
              }
              onClick={() => {
                setAssessmentDetail('delivery');
                setSubjectContent({
                  type: item.type,
                  url: item.url ?? '',
                  title: item.title,
                  contentId: contentId,
                  resourceId: item.resourceId,
                  purpose: item.purpose,
                  purposeText: item.purposeText,
                });
              }}
            >
              {item.title}
            </S.Subtitle>
          </S.DivSubtitleSeparated>
        </S.DivSubtitle>
      ))}
      {(!!examInfo?.generateClosedExam ||
        !!examInfo?.generateDescriptiveExam) && (
        <S.DivSubtitle>
          <S.DivSubtitleSeparated>
            <DescriptionOutlinedIcon
              fontSize="large"
              style={{
                color: theme.colors?.danger,
              }}
            />
            <S.Subtitle
              onClick={() => !hasGenerated && setModalWarningIsOpen(true)}
            >
              {examInfo.generateClosedExam ?? examInfo.generateDescriptiveExam}
            </S.Subtitle>
          </S.DivSubtitleSeparated>
        </S.DivSubtitle>
      )}
      <ModalWarning
        isOpen={modalWarningIsOpen}
        setIsOpen={setModalWarningIsOpen}
        handleConfirm={() => {
          setBlockedContent(true);
          setModalWarningIsOpen(false);
          return handleGenerateExam(
            String(
              examInfo?.generateClosedExam ?? examInfo?.generateDescriptiveExam,
            ),
            Number(
              examInfo?.generateClosedExamId ??
                examInfo?.generateDescriptiveExamId,
            ),
          );
        }}
      />
    </S.Container>
  );
}
